import { useEffect, useState, useCallback, createContext, useContext, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../integrations/supabase/client';
import { authCacheService } from '@/services/auth-cache.service';
import { cleanupPushOnLogout } from '@/hooks/useNativePush';
import type { User, Session } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';

// Types for profile and preferences
interface UserProfile {
  id: string;
  email: string | null;
  display_name: string | null;
  facebook_id: string | null;
  avatar_url: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

interface UserPreferences {
  id: string;
  user_id: string;
  preferred_tees: string | null;
  club_membership: string | null;
  privacy_level: string | null;
  notifications_enabled: boolean | null;
  email_notifications: boolean | null;
  push_notifications: boolean | null;
  units: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  preferences: UserPreferences | null;
  isLoading: boolean;
  error: string | null;
  // FIX: Flag indicating auth is stable and safe for Supabase queries
  // This prevents deadlocks when queries run during auth state transitions
  // See: https://github.com/supabase/supabase-js/issues/1620
  authReady: boolean;
  signIn: (email: string, password: string) => Promise<{ data?: any; error?: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ data?: any; error?: Error | null }>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<{ error?: Error | null }>;
  resetPassword: (token: string, newPassword: string) => Promise<{ error?: Error | null }>;
}

// Create Auth Context
const AuthContext = createContext<AuthState | null>(null);

// AuthProvider component - creates ONE auth listener globally
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  // Track when auth initialization is complete (for gating queries)
  const [authReady, setAuthReady] = useState(false);
  const queryClient = useQueryClient();
  
  // ✅ CRITICAL FIX: Use ref to track current user state (not stale closure)
  const currentUserRef = useRef<User | null>(null);
  currentUserRef.current = user; // Always up to date

  // Listen to auth state changes
  useEffect(() => {
    // ✅ MOBILE FIX: Initialize with cached auth state for instant loading
    const initializeAuth = async () => {
      const cachedAuth = authCacheService.getCachedAuthState();
      if (cachedAuth && cachedAuth.user) {
        // ✅ VALIDATION: Verify cached session is still valid
        // Check if the access token hasn't expired
        const expiresAt = cachedAuth.session?.expires_at;
        const now = Math.floor(Date.now() / 1000);
        
        if (expiresAt && expiresAt > now) {
          // Session is still valid, use it
          setSession(cachedAuth.session);
          setUser(cachedAuth.user);
          setInitializing(false);
          setAuthReady(true);

          return true; // Found valid cached state
        } else {
          // Session expired, clear it
          authCacheService.clearAllCache();
        }
      }
      return false; // No valid cached state
    };

    let hasInitialized = false;
    let lastEventType: string | null = null;
    let lastUserId: string | null = null;
    let lastSessionId: string | null = null;
    
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      // REDUCED: Only log critical auth events in development
      if (import.meta.env.DEV && event === 'SIGNED_OUT') {
        console.log('Auth state change:', event);
      }

      // ENHANCED DEDUPLICATION: Check event type, user ID, and session
      const currentUserId = session?.user?.id || null;
      const currentSessionId = session?.access_token || null;

      // Prevent multiple initial session events
      if (event === 'INITIAL_SESSION' && hasInitialized) {
        if (import.meta.env.DEV) {
          console.log('Ignoring duplicate INITIAL_SESSION event');
        }
        return;
      }

      // Prevent identical auth events (same event type + user + session)
      if (hasInitialized &&
          lastEventType === event &&
          lastUserId === currentUserId &&
          lastSessionId === currentSessionId) {
        if (import.meta.env.DEV) {
          console.log('Ignoring identical auth event:', event);
        }
        return;
      }
      
      // STABILITY FIX: Only update user/session if they've actually changed
      setSession(prevSession => {
        if (prevSession?.access_token !== session?.access_token) {
          return session;
        }
        return prevSession;
      });
      
      setUser(prevUser => {
        if (prevUser?.id !== session?.user?.id) {
          return session?.user ?? null;
        }
        return prevUser;
      });
      
      setInitializing(false);
      lastEventType = event;
      lastUserId = currentUserId;
      lastSessionId = currentSessionId;

      // Mark auth as ready after state change
      setAuthReady(true);

      // Cache auth state for instant page loads (only if session has valid expiry)
      if (session?.user && session?.expires_at) {
        authCacheService.cacheAuthState(session.user, session);
      }

      // Always clear cache on sign out
      if (event === 'SIGNED_OUT') {
        authCacheService.clearAllCache();
      }
      
      // REMOVED: All query invalidations to prevent React Query cascades
      // Query invalidations were causing the re-render loops shown in logs
      
      if (event === 'INITIAL_SESSION') {
        hasInitialized = true;
      }
    });
    
    // ✅ MOBILE FIX: Try cache first, then getSession() only if needed
    initializeAuth().then(hasCachedState => {
      if (hasCachedState) {
        // Cache hit! Session restored instantly
        hasInitialized = true;
        return; // Skip getSession() - we already have valid session
      }
      
      // No cache, need to call getSession()
      // Only do this if user hasn't logged in via onAuthStateChange
      setTimeout(() => {
        // Check if session was set by auth listener (use ref to avoid stale closure)
        if (currentUserRef.current) {
          setInitializing(false);
          hasInitialized = true;
          setAuthReady(true);
          return;
        }

        // No cache, no listener event - call getSession()
        supabase.auth.getSession().then(({ data }) => {
          // Check again if user logged in while getSession() was running
          if (currentUserRef.current) {
            return;
          }

          setSession(data.session);
          setUser(data.session?.user ?? null);
          setInitializing(false);
          hasInitialized = true;
          setAuthReady(true);
        }).catch((error) => {
          console.error('Session check failed:', error);
          setSession(null);
          setUser(null);
          setInitializing(false);
          hasInitialized = true;
          setAuthReady(true);
        });
      }, 100); // Wait 100ms for auth listener to fire first (500ms caused visible latency on coach login)
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []); // No dependencies to prevent multiple setups

  // PARALLEL FETCH: Combined query for profile and preferences (fetched simultaneously)
  const {
    data: userData,
    isLoading: userDataLoading,
    error: userDataError,
  } = useQuery<{ profile: UserProfile | null; preferences: UserPreferences | null } | null, Error>({
    queryKey: ['user-data', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const timerLabel = `🔍 USER-DATA-PARALLEL-FETCH-${Date.now()}`;
      if (import.meta.env.DEV) {
        console.time(timerLabel);
      }

      // Try to get cached data first
      const cachedProfile = authCacheService.getCachedProfile(user.id);

      // MOBILE FIX: Add timeout to prevent infinite hanging on mobile networks
      const timeoutMs = 45000; // Increased to 45s for slower mobile networks
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Profile fetch timeout - please check your connection and try again'));
        }, timeoutMs);
      });

      // Fetch profile and preferences in parallel using Promise.all, with timeout
      const [profileResult, preferencesResult] = await Promise.race([
        Promise.all([
        // Profile fetch
        (async () => {
          const result = cachedProfile
            ? { data: cachedProfile, error: null }
            : await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .maybeSingle();
          return result;
        })(),

        // Preferences fetch (runs simultaneously with profile)
        (async () => {
          const result = await supabase
            .from('user_preferences')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle();
          return result;
        })()
        ]),
        timeoutPromise
      ]);

      if (import.meta.env.DEV) {
        console.timeEnd(timerLabel);
      }

      let profile = profileResult.data;
      let preferences = preferencesResult.data;

      // Handle profile creation if needed
      if (!profile && !profileResult.error) {
        console.log('No profile found, creating new profile for user:', user.id);
        const newProfile = {
          id: user.id,
          email: user.email,
          display_name: user.user_metadata?.full_name || user.email?.split('@')[0] || null,
          avatar_url: user.user_metadata?.avatar_url || null,
          is_public: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        const { data: createdProfile, error: createError } = await supabase
          .from('profiles')
          .insert(newProfile)
          .select()
          .single();

        if (createError) {
          console.error('Failed to create profile:', createError.message);
          throw createError;
        }

        profile = createdProfile;
        authCacheService.cacheProfile(createdProfile);
      } else if (profile) {
        authCacheService.cacheProfile(profile);
      }

      // Handle profile fetch error
      if (profileResult.error && !profile) {
        console.error('Profile fetch error:', profileResult.error.message);
        throw profileResult.error;
      }

      // Handle preferences creation if needed
      if (!preferences && !preferencesResult.error) {
        console.log('No preferences found, creating default preferences for user:', user.id);
        const defaultPreferences = {
          user_id: user.id,
          preferred_tees: 'white',
          club_membership: null,
          privacy_level: 'friends',
          notifications_enabled: true,
          email_notifications: true,
          push_notifications: true,
          units: null,
          updated_at: new Date().toISOString()
        };

        // Use upsert to handle race conditions gracefully
        const { data: createdPrefs, error: createError } = await supabase
          .from('user_preferences')
          .upsert(defaultPreferences, {
            onConflict: 'user_id',
            ignoreDuplicates: false
          })
          .select()
          .single();

        if (createError) {
          console.error('Failed to create/update preferences:', createError.message);
          // Handle various conflict scenarios (409, duplicate key, etc.)
          if (createError.message.includes('duplicate') ||
              createError.message.includes('conflict') ||
              createError.code === '23505' || // PostgreSQL unique violation
              createError.details?.includes('user_preferences_pkey') ||
              createError.details?.includes('user_preferences_user_id_key')) {

            console.log('Preferences conflict detected, retrying fetch...');

            // Add small delay to avoid rapid retries
            await new Promise(resolve => setTimeout(resolve, 100));

            const { data: existingPrefs, error: fetchError } = await supabase
              .from('user_preferences')
              .select('*')
              .eq('user_id', user.id)
              .single();

            if (fetchError) {
              console.error('Failed to fetch existing preferences after conflict:', fetchError.message);
              // If fetch also fails, return null rather than throwing - component can handle gracefully
              preferences = null;
            } else {
              preferences = existingPrefs;
            }
          } else {
            // For non-conflict errors, log but continue with null preferences
            console.error('Non-conflict preference error:', createError);
            preferences = null;
          }
        } else {
          preferences = createdPrefs;
        }
      }

      // Handle preferences fetch error (non-fatal - we can still use the app without preferences)
      if (preferencesResult.error && !preferences) {
        console.error('Preferences fetch error:', preferencesResult.error.message);
        // Don't throw - preferences are optional
        preferences = null;
      }

      return {
        profile: profile as UserProfile | null,
        preferences: preferences as UserPreferences | null
      };
    },
    // MOBILE FIX: Don't block on profile/preferences - let page load first
    enabled: !!user && !initializing,
    // Don't block rendering if this query is slow
    placeholderData: null,
    retry: (failureCount, error) => {
      // Don't retry if it's a permissions, auth, or TIMEOUT error
      if (error?.message?.includes('permission') ||
          error?.message?.includes('auth') ||
          error?.message?.includes('timeout') ||
          error?.message?.includes('Timeout')) {
        return false;
      }
      // Retry up to 2 times for other errors (like network issues, conflicts)
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000), // Exponential backoff
    // Performance optimizations
    staleTime: 1000 * 60 * 10, // 10 minutes - user data rarely changes
    gcTime: 1000 * 60 * 30, // 30 minutes cache time
    refetchOnWindowFocus: false,
  });

  // Extract profile and preferences from combined data
  const profile = userData?.profile ?? null;
  const preferences = userData?.preferences ?? null;
  const profileLoading = userDataLoading;
  const prefsLoading = userDataLoading;
  const profileError = userDataError;
  const prefsError = userDataError;

  // Sign in function
  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    const normalizedEmail = email.trim().toLowerCase();

    const { data, error } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });

    if (error) {
      setError(error.message);

      // SECURITY: Log failed authentication attempts to Sentry for monitoring
      // This helps detect brute force attacks and credential stuffing
      Sentry.captureMessage('Authentication failed', {
        level: 'warning',
        tags: {
          security_relevant: 'true',
          error_category: 'auth',
          auth_event: 'sign_in_failed',
        },
        extra: {
          // Only log email domain, not full address (privacy)
          email_domain: normalizedEmail.split('@')[1] || 'unknown',
          error_code: error.message,
          timestamp: new Date().toISOString(),
        },
      });

      return { error };
    }

    return { data, error: null };
  }, [queryClient]);

  // Sign up function
  const signUp = useCallback(async (email: string, password: string) => {
    setError(null);

    try {
      // Step 1: Check if this email was bulk imported (smart registration)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        const errorMessage = 'Missing Supabase configuration';
        setError(errorMessage);
        return { error: new Error(errorMessage) };
      }

      // Forward a setup_token if the user arrived via an invitation link
      // (?token=...). The server requires it to activate a bulk-imported
      // account — activating on email match alone is an account-takeover vector.
      const setupToken = new URLSearchParams(window.location.search).get('token') || undefined;

      const smartRegResponse = await fetch(`${supabaseUrl}/functions/v1/smart_registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`
        },
        body: JSON.stringify({ email, password, setup_token: setupToken })
      });

      console.log('🔍 Smart registration response status:', smartRegResponse.status);
      const smartRegData = await smartRegResponse.json();
      console.log('🔍 Smart registration data:', smartRegData);

      if (!smartRegResponse.ok) {
        // Edge function returned an error
        const errorMessage = smartRegData.error || 'Registration failed';
        console.log('❌ Smart registration error:', errorMessage);
        setError(errorMessage);

        // SECURITY: Log failed registration attempts to Sentry
        Sentry.captureMessage('Registration failed', {
          level: 'warning',
          tags: {
            security_relevant: 'true',
            error_category: 'auth',
            auth_event: 'sign_up_failed',
          },
          extra: {
            email_domain: email.split('@')[1] || 'unknown',
            error_code: errorMessage,
            timestamp: new Date().toISOString(),
          },
        });

        return { error: new Error(errorMessage) };
      }

      // Step 2: Check if account was activated from bulk import
      if (smartRegData.activated) {
        console.log('✅ Activated bulk-imported account, signing in...');

        // Account was activated! Now sign them in with the password they just set
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (signInError) {
          setError(signInError.message);
          return { error: signInError };
        }

        return { data: signInData, error: null };
      }

      // Step 3: Normal registration (no bulk import found)
      console.log('✅ No bulk import found, proceeding with normal registration');
      console.log('   activated:', smartRegData.activated);
      console.log('   created:', smartRegData.created);

      const { data, error } = await supabase.auth.signUp({ email, password });

      if (error) {
        setError(error.message);
        return { error };
      }

      return { data, error: null };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setError(errorMessage);
      return { error: error instanceof Error ? error : new Error(errorMessage) };
    }
  }, []);

  // Sign out function
  const signOut = useCallback(async () => {
    setError(null);

    // Clean up push notification token before invalidating the session
    await cleanupPushOnLogout().catch(() => {});

    const { error } = await supabase.auth.signOut();
    if (error) setError(error.message);

    // Clear all caches on sign out
    authCacheService.clearAllCache();
    queryClient.clear();
    
    console.log('✅ Logout complete');
  }, [queryClient]);

  // Forgot password function
  const forgotPassword = useCallback(async (email: string) => {
    setError(null);
    try {
      const { AuthService } = await import('@/services/api/auth.service');
      const authService = new AuthService();
      const result = await authService.forgotPassword(email);
      
      if (result.error) {
        const err: any = result.error;
        const errorMsg = err instanceof Error ? err.message : String(err);
        setError(errorMsg);
        return { error: err instanceof Error ? err : new Error(String(err)) };
      }
      return { error: null };
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Unknown error');
      setError(errorObj.message);
      return { error: errorObj };
    }
  }, []);

  // Reset password function
  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    setError(null);
    try {
      const { AuthService } = await import('@/services/api/auth.service');
      const authService = new AuthService();
      const result = await authService.resetPassword(token, newPassword);
      
      if (result.error) {
        const err: any = result.error;
        const errorMsg = err instanceof Error ? err.message : String(err);
        setError(errorMsg);
        return { error: err instanceof Error ? err : new Error(String(err)) };
      }
      return { error: null };
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Unknown error');
      setError(errorObj.message);
      return { error: errorObj };
    }
  }, []);

  // FOUNDATION FIX: Memoize auth value to prevent object recreation on every render
  const authValue: AuthState = useMemo(() => {
    return {
      user,
      session,
      profile: profile ?? null,
      preferences: preferences ?? null,
      // MOBILE FIX: Don't wait for profile/preferences - only wait for auth check
      // This allows page to load immediately while profile data loads in background
      isLoading: initializing,
      error: error || profileError?.message || prefsError?.message || null,
      // FIX: Expose authReady for components to gate queries
      // This prevents GoTrueClient lock deadlocks on Safari
      authReady,
      signIn,
      signUp,
      signOut,
      forgotPassword,
      resetPassword,
    };
  }, [
    user,
    session,
    profile,
    preferences,
    initializing,
    profileLoading,
    prefsLoading,
    error,
    profileError?.message,
    prefsError?.message,
    authReady,
    signIn,
    signUp,
    signOut,
    forgotPassword,
    resetPassword
  ]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Consumer hook - uses the context instead of creating new listeners
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  
  // SAFE FALLBACK: Don't throw error during SSR or concurrent rendering
  // Return loading state instead to prevent crashes
  if (!context) {
    console.warn('useAuth called before AuthProvider is ready, returning loading state');
    return {
      user: null,
      session: null,
      profile: null,
      preferences: null,
      isLoading: true, // Still loading context
      error: null,
      authReady: false, // Not ready until context is available
      signIn: async () => ({ error: new Error('Auth context not ready') }),
      signUp: async () => ({ error: new Error('Auth context not ready') }),
      signOut: async () => {},
      forgotPassword: async () => ({ error: new Error('Auth context not ready') }),
      resetPassword: async () => ({ error: new Error('Auth context not ready') }),
    };
  }
  
  return context;
}
